import './KbArticleEditAssets.css'
import { PUB_CDN } from '@/utilities/envVariables'
import { useApi } from '@/hooks/useApi'
import { useCallback, useEffect, useRef, useState } from 'react'
import { kbArticleAssetsDeleteSpec, kbArticleAssetsListSpec, kbArticleAssetsSaveSpec, type KbArticleAssetsListItem } from 'aevocam-rest-api-spec'
import { toastError, toastSuccess } from '@/components/action/Toaster'
import { Spinner } from '@/components/graphics/Spinner'
import { Icon } from '@/components/graphics/Icon'
import { IconButton } from '@/components/action/IconButton'

type Props = {
  /** Article ID from the editor; assets load only when this is set and not the "new" sentinel (-1n). */
  articleId: bigint
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      const base64 = dataUrl.replace(/^data:[^;]+;base64,/, '')
      resolve(base64)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export function KbArticleEditAssets({ articleId }: Props) {
  const api = useApi({ requireLogin: true, adminOnly: true })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [assets, setAssets] = useState<KbArticleAssetsListItem[]>([])
  const [loading, setLoading] = useState(false)

  //--------------------------------------------------------------------------------
  const loadAssets = useCallback(async () => {
    if (articleId == null || articleId === -1n) return
    setLoading(true)
    const res = await api.request(kbArticleAssetsListSpec, {
      path: { article: String(articleId) },
      query: { withContentTypes: 'true' },
    })
    setLoading(false)
    if (res.status === 200 && res.data?.assets) {
      setAssets(res.data.assets)
    } else {
      toastError(res.error ?? 'Failed to load assets')
    }
  }, [articleId, api])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (articleId == null || articleId === -1n) {
      setAssets([])
      return
    }
    void loadAssets()
  }, [articleId, loadAssets])

  //--------------------------------------------------------------------------------
  async function handleAssetClick(asset: KbArticleAssetsListItem) {
    const friendlyName = asset.name.replace(/\.[^.]+$/, '')
    const markdown = `![${friendlyName}](@/${encodeURIComponent(asset.name)})`
    try {
      await navigator.clipboard.writeText(markdown)
      toastSuccess('Copied to clipboard')
    } catch {
      toastError('Failed to copy to clipboard')
    }
  }

  //--------------------------------------------------------------------------------
  function handleUploadClick() {
    fileInputRef.current?.click()
  }

  //--------------------------------------------------------------------------------
  async function handleFileInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const files = ev.target.files
    if (!files?.length) return
    try {

      const assets = await Promise.all(
        Array.from(files).map(async (file) => ({
          name: file.name,
          contentType: file.type || undefined,
          content: await readFileAsBase64(file),
        }))
      )

      const res = await api.request(kbArticleAssetsSaveSpec, {
        path: { article: String(articleId) },
        body: { assets },
      })

      if (res.status === 200) {
        toastSuccess(assets.length === 1 ? 'Uploaded 1 asset' : `Uploaded ${assets.length} assets`)
        await loadAssets()
      } else {
        toastError(res.error ?? 'Upload failed')
      }

    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      ev.target.value = ''  // Reset it so the user can re-upload
    }
  }

  //--------------------------------------------------------------------------------
  async function handleDeleteAsset(asset: KbArticleAssetsListItem, ev: React.MouseEvent) {
    ev.stopPropagation()
    if (!confirm(`Delete "${asset.name}"?`)) return
    try {
      const res = await api.request(kbArticleAssetsDeleteSpec, {
        path: { article: String(articleId) },
        body: { names: [asset.name] },
      })
      if (res.status === 200) {
        toastSuccess('Asset deleted')
        await loadAssets()
      } else {
        toastError(res.error ?? 'Delete failed')
      }
    } catch (e) {
      toastError(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  //--------------------------------------------------------------------------------
  // Render

  if (articleId == null || articleId === -1n) return null

  if (loading) {
    return (
      <div className='KbArticleEditAssets Loading'>
        <Spinner />
        <span>Loading</span>
      </div>
    )
  }

  return (
    <div className='KbArticleEditAssets'>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept='*/*'
        className='FileInput'
        aria-hidden
        onChange={handleFileInputChange}
      />
      <div className='List'>

        {assets.map((asset) => (
          <div
            key={asset.name}
            className='Item'
            role='button'
            tabIndex={0}
            onClick={() => handleAssetClick(asset)}
            onKeyDown={(e) => e.key === 'Enter' && handleAssetClick(asset)}
          >

            <div className='Thumb'>

              {isImage(asset) ? (
                <img
                  src={assetImageUrl(articleId, asset.name)}
                  alt={asset.name}
                  loading='lazy'
                />
              ) : (
                <div className='Placeholder' aria-hidden>
                  <Icon icon='file' />
                </div>
              )}

              <IconButton
                icon='times'
                className='Delete'
                tooltip='Delete asset'
                aria-label='Delete asset'
                onClick={(ev) => handleDeleteAsset(asset, ev)}
              />

            </div>

            <div className='Name' title={asset.name}>
              {asset.name}
            </div>

          </div>
        ))}

        <div
          className='Item UploadItem'
          role='button'
          tabIndex={0}
          onClick={handleUploadClick}
          onKeyDown={(e) => e.key === 'Enter' && handleUploadClick()}
        >
          <div className='Thumb'>
            <Icon icon='fileUpload' aria-hidden />
          </div>
          <div className='Name'>Upload</div>
        </div>

      </div>
    </div>
  )
}


//--------------------------------------------------------------------------------
function isImage(asset: KbArticleAssetsListItem): boolean {
  const ct = asset.contentType ?? ''
  return ct.startsWith('image/')
}

//--------------------------------------------------------------------------------
function assetImageUrl(articleId: bigint, name: string): string {
  return `${PUB_CDN}/article/${articleId}/asset/${encodeURIComponent(name)}`
}
